import { useState } from 'react';
import { X, Users, Utensils, DivideCircle, Check } from 'lucide-react';
import clsx from 'clsx';

interface OrderItem {
  id: string;
  menuItemId: string;
  name: string;
  shortName?: string;
  priceCents: number;
  quantity: number;
  seatNumber?: number;
}

interface SplitConfig {
  type: 'even' | 'by_seat' | 'by_item' | 'custom';
  splits: number;
  itemAssignments?: Record<string, number>; // itemId -> splitIndex
  amounts?: number[]; // For custom splits
}

interface SplitCheckModalProps {
  items: OrderItem[];
  subtotal: number;
  tax: number;
  total: number;
  partySize: number;
  onConfirm: (config: SplitConfig) => void;
  onCancel: () => void;
}

export default function SplitCheckModal({
  items,
  subtotal,
  tax,
  total,
  partySize,
  onConfirm,
  onCancel,
}: SplitCheckModalProps) {
  const [splitType, setSplitType] = useState<'even' | 'by_seat' | 'by_item' | 'custom'>('even');
  const [splitCount, setSplitCount] = useState(2);
  const [itemAssignments, setItemAssignments] = useState<Record<string, number>>({});
  const [customAmounts, setCustomAmounts] = useState<string[]>(['', '']);

  const formatCurrency = (cents: number) => `$${(cents / 100).toFixed(2)}`;

  const handleSplitCountChange = (count: number) => {
    setSplitCount(count);
    setCustomAmounts(Array(count).fill(''));
  };

  const assignItemToSplit = (itemId: string, splitIndex: number) => {
    setItemAssignments((prev) => ({
      ...prev,
      [itemId]: splitIndex,
    }));
  };

  // Helper to add proportional tax to subtotals
  const addProportionalTax = (splitSubtotals: number[]): number[] => {
    const totalSubtotal = splitSubtotals.reduce((sum, amt) => sum + amt, 0);
    if (totalSubtotal === 0) return splitSubtotals;

    // Allocate tax proportionally, ensuring totals add up correctly
    let remainingTax = tax;
    return splitSubtotals.map((splitSubtotal, idx) => {
      if (idx === splitSubtotals.length - 1) {
        // Last split gets remaining tax to avoid rounding errors
        return splitSubtotal + remainingTax;
      }
      const proportionalTax = Math.round((splitSubtotal / totalSubtotal) * tax);
      remainingTax -= proportionalTax;
      return splitSubtotal + proportionalTax;
    });
  };

  const calculateSplitAmounts = () => {
    if (splitType === 'even') {
      // Even split - distribute total evenly, last split gets remainder
      const baseAmount = Math.floor(total / splitCount);
      const remainder = total - (baseAmount * splitCount);
      return Array.from({ length: splitCount }, (_, idx) =>
        idx === splitCount - 1 ? baseAmount + remainder : baseAmount
      );
    }

    if (splitType === 'by_seat') {
      // Group by seat (subtotals only)
      const seatSubtotals: Record<number, number> = {};
      items.forEach((item) => {
        const seat = item.seatNumber || 1;
        seatSubtotals[seat] = (seatSubtotals[seat] || 0) + item.priceCents * item.quantity;
      });
      // Add proportional tax to each seat's subtotal
      return addProportionalTax(Object.values(seatSubtotals));
    }

    if (splitType === 'by_item') {
      // Group by assigned split (subtotals only)
      const splitSubtotals = Array(splitCount).fill(0);
      items.forEach((item) => {
        const splitIndex = itemAssignments[item.id] ?? 0;
        splitSubtotals[splitIndex] += item.priceCents * item.quantity;
      });
      // Add proportional tax to each split's subtotal
      return addProportionalTax(splitSubtotals);
    }

    if (splitType === 'custom') {
      return customAmounts.map((amt) => Math.round(parseFloat(amt || '0') * 100));
    }

    return [];
  };

  const splitAmounts = calculateSplitAmounts();
  const totalAssigned = splitAmounts.reduce((sum, amt) => sum + amt, 0);
  const remaining = total - totalAssigned;

  const handleConfirm = () => {
    onConfirm({
      type: splitType,
      splits: splitCount,
      itemAssignments: splitType === 'by_item' ? itemAssignments : undefined,
      amounts: splitAmounts,
    });
  };

  const uniqueSeats = [...new Set(items.map((i) => i.seatNumber || 1))].sort();

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] max-h-[90dvh] flex flex-col shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h2 className="text-xl font-bold text-slate-900">Split Check</h2>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-slate-100 rounded-full transition-colors"
          >
            <X className="w-6 h-6 text-slate-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          {/* Split Type Selection */}
          <div>
            <h3 className="font-semibold text-slate-900 mb-3">How to split?</h3>
            <div className="grid grid-cols-2 gap-3">
              {[
                { id: 'even', label: 'Split Evenly', icon: DivideCircle },
                { id: 'by_seat', label: 'By Seat', icon: Users },
                { id: 'by_item', label: 'By Item', icon: Utensils },
                { id: 'custom', label: 'Custom Amounts', icon: DivideCircle },
              ].map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setSplitType(id as typeof splitType)}
                  className={clsx(
                    'p-4 rounded-lg border-2 flex flex-col items-center gap-2 transition-colors',
                    splitType === id
                      ? 'border-primary-600 bg-primary-50 text-primary-700'
                      : 'border-slate-200 text-slate-700 hover:border-slate-300'
                  )}
                >
                  <Icon className="w-6 h-6" />
                  <span className="text-sm font-medium">{label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Split Count (for even and by_item) */}
          {(splitType === 'even' || splitType === 'by_item' || splitType === 'custom') && (
            <div>
              <h3 className="font-semibold text-slate-900 mb-3">Number of splits</h3>
              <div className="flex gap-2">
                {[2, 3, 4, 5, 6].map((count) => (
                  <button
                    key={count}
                    onClick={() => handleSplitCountChange(count)}
                    className={clsx(
                      'w-12 h-12 rounded-lg font-semibold transition-colors',
                      splitCount === count
                        ? 'bg-primary-600 text-white'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    )}
                  >
                    {count}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* By Item Assignment */}
          {splitType === 'by_item' && (
            <div>
              <h3 className="font-semibold text-slate-900 mb-3">Assign items to splits</h3>
              <div className="space-y-2">
                {items.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                  >
                    <div>
                      <p className="font-medium text-slate-900">
                        {item.quantity}x {item.shortName || item.name}
                      </p>
                      <p className="text-sm text-slate-500">
                        {formatCurrency(item.priceCents * item.quantity)}
                      </p>
                    </div>
                    <div className="flex gap-1">
                      {Array.from({ length: splitCount }).map((_, idx) => (
                        <button
                          key={idx}
                          onClick={() => assignItemToSplit(item.id, idx)}
                          className={clsx(
                            'w-8 h-8 rounded-full text-sm font-medium transition-colors',
                            (itemAssignments[item.id] ?? 0) === idx
                              ? 'bg-primary-600 text-white'
                              : 'bg-white border border-slate-200 text-slate-600'
                          )}
                        >
                          {idx + 1}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* By Seat Display */}
          {splitType === 'by_seat' && (
            <div>
              <h3 className="font-semibold text-slate-900 mb-3">Split by seat</h3>
              <div className="space-y-2">
                {uniqueSeats.map((seat, idx) => {
                  const seatItems = items.filter((i) => (i.seatNumber || 1) === seat);
                  // Use the calculated amount which includes tax
                  const seatTotal = splitAmounts[idx] || 0;
                  return (
                    <div key={seat} className="p-3 bg-slate-50 rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-semibold">Seat {seat}</span>
                        <span className="font-semibold text-primary-600">
                          {formatCurrency(seatTotal)}
                        </span>
                      </div>
                      <div className="text-sm text-slate-500">
                        {seatItems.map((i) => i.shortName || i.name).join(', ')}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Custom Amounts */}
          {splitType === 'custom' && (
            <div>
              <h3 className="font-semibold text-slate-900 mb-3">Enter amounts</h3>
              <div className="space-y-2">
                {customAmounts.map((amount, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <span className="w-16 text-sm font-medium text-slate-600">
                      Split {idx + 1}
                    </span>
                    <div className="flex-1 relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
                        $
                      </span>
                      <input
                        type="number"
                        step="0.01"
                        value={amount}
                        onChange={(e) => {
                          const newAmounts = [...customAmounts];
                          newAmounts[idx] = e.target.value;
                          setCustomAmounts(newAmounts);
                        }}
                        className="input pl-7 w-full"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                ))}
                {remaining !== 0 && (
                  <p
                    className={clsx(
                      'text-sm font-medium mt-2',
                      remaining > 0 ? 'text-amber-600' : 'text-danger-600'
                    )}
                  >
                    {remaining > 0
                      ? `${formatCurrency(remaining)} remaining`
                      : `${formatCurrency(Math.abs(remaining))} over total`}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Preview */}
          <div className="bg-slate-100 rounded-lg p-4">
            <h3 className="font-semibold text-slate-900 mb-3">Preview</h3>
            <div className="space-y-2">
              {splitAmounts.map((amount, idx) => (
                <div key={idx} className="flex justify-between">
                  <span className="text-slate-600">
                    {splitType === 'by_seat' ? `Seat ${uniqueSeats[idx]}` : `Split ${idx + 1}`}
                  </span>
                  <span className="font-semibold">{formatCurrency(amount)}</span>
                </div>
              ))}
              <div className="border-t pt-2 flex justify-between font-semibold">
                <span>Total</span>
                <span>{formatCurrency(total)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-200 flex gap-3">
          <button onClick={onCancel} className="btn btn-secondary flex-1">
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={splitType === 'custom' && remaining !== 0}
            className="btn btn-primary flex-1 gap-2 disabled:opacity-50"
          >
            <Check className="w-5 h-5" />
            Apply Split
          </button>
        </div>
      </div>
    </div>
  );
}
